import { AutoAwesome, CalendarToday as CalendarIcon, DeleteOutline as DeleteIcon, EmailOutlined as EmailIcon, FlightTakeoff as FlightIcon, Hotel as HotelIcon, LaunchOutlined as ExternalLinkIcon, LocationOn as LocationIcon, Remove as MinimizeIcon, MoreHoriz as MoreHorizIcon, People as PeopleIcon, Style as StyleIcon, Train as TrainIcon, Verified as VerifiedIcon } from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Box, Button, Chip, CircularProgress, FormControl, FormControlLabel, FormLabel, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import yovoAssistIcon from '../../assets/brand/yovo-assist-icon.png';
import playicon from '../../assets/PlayIcon.png';
import womanIconWithoutBg from '../../assets/woman_icon_without_bg.png';
import { API_BASE_URL } from '../../config/backendConfig';
import {
  YOVO_ASSIST_WELCOME,
  getEnabledQuickActions,
  isYovoAssistWelcomeText,
} from '../../config/yovoAssistEmptyState';
import { chatbotService, clearChatbotSession, fallbackResponses } from '../../services/chatbotApi';

/** Yovo chatbot brand accent */
const YOVO_CHAT_ACCENT = '#CF2048';
/** Chat body — soft blue → white → blush (Figma empty-state canvas) */
const YOVO_CHAT_BODY_BG =
  'linear-gradient(135deg, #EAF5FC 0%, #F7F8FB 48%, #F6EAF2 100%)';
/** Welcome bubble — sky blue → coral/pink (Figma) */
const YOVO_WELCOME_BUBBLE_BG =
  'linear-gradient(90deg, #B5E0F5 0%, #E8C8E8 45%, #F2B8BC 100%)';
/** Homepage Assist trigger — shared gradient stroke (purple → cyan → violet → red) */
const YOVO_ASSIST_INPUT_BORDER_GRAD =
  'linear-gradient(-4deg, rgba(187, 0, 255, 0.44) 0%, rgba(0, 178, 255, 0.44) 30%, rgba(108, 39, 255, 0.44) 65%, rgba(255, 0, 0, 0.44) 100%)';
/** Input / accent stroke — same blue → pink (legacy accents) */
const YOVO_ASSIST_STROKE_GRAD =
  'linear-gradient(90deg, #7EC8E8 0%, #E8A0A8 100%)';
/** Shared CTA fill — soft canvas tint */
const YOVO_CHAT_CTA_SX = {
  backgroundImage: YOVO_CHAT_BODY_BG,
  backgroundColor: '#F7F8FB',
  color: YOVO_CHAT_ACCENT,
  border: '1px solid rgba(207, 32, 72, 0.22)',
  boxShadow: 'none',
  transition: 'opacity 0.15s ease',
  '&:hover': {
    backgroundImage: YOVO_CHAT_BODY_BG,
    backgroundColor: '#F7F8FB',
    boxShadow: 'none',
    opacity: 0.92,
  },
};
/** Content cards / bubbles / input */
const YOVO_CHAT_RADIUS = '8px';
/** Outer floating shell */
const YOVO_CHAT_SHELL_RADIUS = '12px';
/** Chips / small controls */
const YOVO_CHAT_CHIP_RADIUS = '6px';

const isLegacyWelcomeText = (text) =>
  /personal travel guide|I'm Yovo,/i.test(String(text || '')) ||
  /Hello! I'm Yovo/i.test(String(text || ''));

const isWelcomeBotMessage = (msg) =>
  msg?.from === 'bot'
  && (isYovoAssistWelcomeText(msg?.text) || isLegacyWelcomeText(msg?.text));

/** Empty-state hero: lady + blue→pink welcome bubble (Figma) */
const EmptyStateWelcomeHero = memo(() => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      px: 0.25,
      pt: 0.25,
      pb: 0.5,
      width: '100%',
      boxSizing: 'border-box',
    }}
  >
    <Box
      component="img"
      src={womanIconWithoutBg}
      alt=""
      sx={{
        width: 'auto',
        height: { xs: 96, sm: 108 },
        maxWidth: '34%',
        objectFit: 'contain',
        objectPosition: 'bottom center',
        flexShrink: 0,
        userSelect: 'none',
        pointerEvents: 'none',
        ml: -0.5,
      }}
    />
    <Box
      sx={{
        flex: '0 1 auto',
        width: 'fit-content',
        maxWidth: '62%',
        background: YOVO_WELCOME_BUBBLE_BG,
        borderRadius: YOVO_CHAT_RADIUS,
        px: 1.25,
        py: 0.85,
        boxShadow: '0 2px 8px rgba(120, 100, 140, 0.08)',
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Red Hat Display", -apple-system, Roboto, Helvetica, sans-serif',
          fontSize: '12.5px',
          fontWeight: 600,
          color: '#1A1A1A',
          lineHeight: 1.3,
          whiteSpace: 'pre-line',
        }}
      >
        {YOVO_ASSIST_WELCOME}
      </Typography>
    </Box>
  </Box>
));

const EmptyStateQuickActions = memo(({ onSelect }) => {
  const actions = getEnabledQuickActions();
  if (!actions.length) return null;
  return (
  <Box
    sx={{
      flexShrink: 0,
      px: 0.5,
      pb: 0.5,
      pt: 1,
    }}
  >
    <Box
      sx={{
        borderRadius: YOVO_CHAT_RADIUS,
        border: '1px solid rgba(220, 200, 220, 0.55)',
        backgroundColor: 'rgba(255, 255, 255, 0.72)',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(80, 40, 90, 0.05)',
      }}
    >
      {actions.map((action, idx) => (
        <Box
          key={action.id}
          role="button"
          tabIndex={0}
          onClick={() => onSelect(action)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(action);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.5,
            py: '14px',
            cursor: 'pointer',
            borderBottom:
              idx < actions.length - 1
                ? '1px solid rgba(220, 200, 220, 0.45)'
                : 'none',
            transition: 'background-color 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 245, 248, 0.95)',
            },
            '&:focus-visible': {
              outline: `2px solid ${YOVO_CHAT_ACCENT}`,
              outlineOffset: -2,
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Red Hat Display", -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '13px',
              fontWeight: 500,
              color: '#1A1A1A',
              lineHeight: 1.35,
              flex: 1,
              minWidth: 0,
            }}
          >
            {action.label}
          </Typography>
          <ChevronRightIcon
            sx={{ fontSize: 18, color: 'rgba(26, 26, 26, 0.45)', flexShrink: 0 }}
            aria-hidden
          />
        </Box>
      ))}
    </Box>
  </Box>
  );
});

/** Waiting / generating — left-aligned on gradient canvas (no card) */
const YovoWorkingIndicator = memo(() => (
  <Box
    role="status"
    aria-live="polite"
    aria-label="Yovo is thinking"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 0.75,
      py: 0.25,
      maxWidth: '100%',
    }}
  >
    <AutoAwesome
      sx={{
        fontSize: 14,
        color: YOVO_CHAT_ACCENT,
        flexShrink: 0,
      }}
      aria-hidden
    />
    <Typography
      component="span"
      sx={{
        fontFamily: '"Red Hat Display", -apple-system, Roboto, Helvetica, sans-serif',
        fontSize: '13px',
        fontWeight: 600,
        color: '#1A1A1A',
        lineHeight: 1.35,
      }}
    >
      Yovo is thinking
    </Typography>
    <Box
      aria-hidden
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        ml: 0.25,
        '@keyframes yovoWorkDot': {
          '0%, 80%, 100%': { opacity: 0.22 },
          '40%': { opacity: 1 },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& > span': {
            animation: 'none !important',
            opacity: 0.65,
          },
        },
      }}
    >
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          component="span"
          sx={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            bgcolor: YOVO_CHAT_ACCENT,
            display: 'inline-block',
            animation: 'yovoWorkDot 1.05s ease-in-out infinite',
            animationDelay: `${i * 180}ms`,
          }}
        />
      ))}
    </Box>
  </Box>
));

/**
 * Normalize list / markdown so chat bubbles never show broken steps
 * (e.g. missing words after **bold**, bare "1." lines, glued "…?2.").
 */
function normalizeChatMessageFormatting(text) {
  if (text == null || text === '') return text;
  let t = String(text);

  t = t
    .replace(/[\u201C\u201D\u00AB\u00BB]/g, '"')
    .replace(/[\u2018\u2019\u2032]/g, "'")
    .replace(/[\u2013\u2014]/g, '—');

  // Preserve itinerary day headers for bold UI
  const dayHeaders = [];
  t = t.replace(/\*\*((?:Day\s+\d+)[^*]*)\*\*/gi, (_, inner) => {
    const token = `%%YOVO_DAY_${dayHeaders.length}%%`;
    dayHeaders.push(inner.trim());
    return token;
  });

  // Keep inner text of other **bold** / __bold__; never delete it
  t = t.replace(/\*\*([^*]+)\*\*/g, '$1');
  t = t.replace(/__([^_]+)__/g, '$1');
  t = t.replace(/\*\*/g, '');

  dayHeaders.forEach((inner, i) => {
    t = t.replace(`%%YOVO_DAY_${i}%%`, `**${inner}**`);
  });

  t = t.replace(/^#{1,6}\s+/gm, '');
  t = t.replace(/([?!.…])\s*(\d{1,2})\.\s+/g, '$1\n$2. ');
  t = t.replace(/^(\d{1,2})[)\]]\s+/gm, '$1. ');
  t = t.replace(/^(\d{1,2})\s+[-–—]\s+/gm, '$1. ');
  t = t.replace(/^(\d{1,2})\.\s*\n+(?!\d{1,2}\.\s)/gm, '$1. ');
  t = t.replace(/^([•\-*])\s*\n+(?![•\-*\d])/gm, '$1 ');
  t = t.replace(/^(\d{1,2})\.\s+/gm, '$1. ');
  t = t.replace(/([^\n])\n(\d{1,2}\.\s+\S)/g, '$1\n\n$2');
  t = t.replace(/^\d{1,2}\.\s*$/gm, '');
  t = t.replace(/[ \t]+\n/g, '\n');
  t = t.replace(/\n{3,}/g, '\n\n');
  t = t.replace(/[ \t]{2,}/g, ' ');

  return t.trim();
}

const YOVO_SUPPORT_EMAIL = 'support@yovotrip.com';

/** Real mobile UA only — do NOT use pointer:coarse (touch laptops falsely hit mailto → Edge downloads). */
function isMobileMailClientPreferred() {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
    navigator.userAgent || ''
  );
}

function buildGmailComposeUrl({ to, subject, body } = {}) {
  const email = to || YOVO_SUPPORT_EMAIL;
  const su = subject || 'YovoTrip Support Request';
  const msg =
    body
    || 'Hi YovoTrip Support,\n\nPNR / Booking ID: \n\nMy query:\n\n';
  return (
    'https://mail.google.com/mail/?view=cm&fs=1'
    + `&to=${encodeURIComponent(email)}`
    + `&su=${encodeURIComponent(su)}`
    + `&body=${encodeURIComponent(msg)}`
  );
}

/** Hide leaked structured LLM markers if server strip missed them */
function stripLeakedStructuredMarkers(text) {
  if (text == null || text === '') return text;
  return normalizeChatMessageFormatting(
    String(text)
      .replace(/\{\s*"trip_brief"\s*:[\s\S]*$/i, '')
      .replace(/\{?\s*"?action"\s*:\s*"(?:flight_search|hotel_search|train_search|flight|hotel|train)"[\s\S]*$/i, '')
      .replace(/\[?\s*REFIN(?:EMENTS)?\s*:\s*[^\]\n]*\]?/gi, '')
  );
}

/**
 * Split into display lines after normalization.
 */
function expandMessageLines(text) {
  if (text == null || text === '') return [];
  return normalizeChatMessageFormatting(String(text)).split('\n');
}

/**
 * Render remaining inline emphasis if any (usually already normalized away).
 * Email addresses are clickable (Gmail compose on desktop).
 */
function renderInlineMarkdown(text) {
  if (text == null || text === '') return text;
  const str = String(text);
  const boldParts = str.split(/(\*\*[^*]+\*\*)/g);
  const nodes = [];
  boldParts.forEach((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    if (m) {
      nodes.push(
        <Box component="strong" key={`b-${i}`} sx={{ fontWeight: 700 }}>
          {renderEmailLinks(m[1], `b-${i}`)}
        </Box>
      );
      return;
    }
    nodes.push(...[].concat(renderEmailLinks(part, `t-${i}`)));
  });
  return nodes.length === 1 && typeof nodes[0] === 'string' ? nodes[0] : nodes;
}

function renderEmailLinks(text, keyPrefix) {
  if (text == null || text === '') return text;
  const str = String(text);
  const EMAIL_RE = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  const parts = str.split(EMAIL_RE);
  if (parts.length === 1) return str;
  return parts.map((part, i) => {
    if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(part)) {
      return (
        <Box
          component="a"
          key={`${keyPrefix}-e-${i}`}
          href={`mailto:${part}`}
          onClick={(e) => {
            if (!isMobileMailClientPreferred()) {
              e.preventDefault();
              window.open(
                buildGmailComposeUrl({ to: part, subject: 'YovoTrip Support Request' }),
                '_blank',
                'noopener,noreferrer'
              );
            }
          }}
          sx={{
            color: YOVO_CHAT_ACCENT,
            fontWeight: 600,
            textDecoration: 'underline',
            wordBreak: 'break-all',
          }}
        >
          {part}
        </Box>
      );
    }
    return part;
  });
}

function parseMailto(mailto) {
  if (!mailto || typeof mailto !== 'string' || !mailto.toLowerCase().startsWith('mailto:')) {
    return null;
  }
  try {
    const withoutScheme = mailto.slice('mailto:'.length);
    const [addrPart, query = ''] = withoutScheme.split('?');
    const params = new URLSearchParams(query);
    return {
      to: decodeURIComponent(addrPart.split(',')[0].trim()),
      subject: params.get('subject') || '',
      body: params.get('body') || '',
    };
  } catch {
    return null;
  }
}

function resolveGmailComposeUrl(action) {
  const rawUrl = action?.url || '';
  if (/^https?:\/\//i.test(rawUrl) && /mail\.google\.com/i.test(rawUrl)) {
    return rawUrl;
  }
  if (/^https?:\/\//i.test(rawUrl) && !/^mailto:/i.test(rawUrl)) {
    return rawUrl;
  }

  const fromMailto = parseMailto(action?.mailto);
  return buildGmailComposeUrl({
    to: action?.email || fromMailto?.to || YOVO_SUPPORT_EMAIL,
    subject: fromMailto?.subject,
    body: fromMailto?.body,
  });
}

function openEmailSupportAction(action) {
  // Mobile only: OS mail app via mailto
  if (isMobileMailClientPreferred() && action?.mailto) {
    window.location.href = action.mailto;
    return;
  }

  // Desktop / Edge / Linux: HTTPS Gmail compose only — never mailto (downloads .eml / blank file)
  const gmailUrl = resolveGmailComposeUrl(action);
  const win = window.open(gmailUrl, '_blank');
  if (win) {
    try {
      win.opener = null;
    } catch { /* ignore */ }
  }
}

// Plan Style options
const PLAN_STYLES = [
  { id: 'relaxed', label: 'Relaxed', icon: '😌' },
  { id: 'family', label: 'Family-friendly', icon: '👨‍👩‍👧' },
  { id: 'elderly', label: 'Elderly-friendly', icon: '👴' },
  { id: 'budget', label: 'Budget', icon: '💰' },
  { id: 'romantic', label: 'Romantic', icon: '💕' },
  { id: 'pilgrimage', label: 'Pilgrimage', icon: '🙏' },
  { id: 'food', label: 'Food-focused', icon: '🍜' },
  { id: 'luxury', label: 'Luxury', icon: '✨' },
  { id: 'weekend', label: 'Weekend', icon: '📅' },
];

// Trip Brief Card Component — compact Figma card (~110–125px)
const TripBriefCard = memo(({ brief, onClose }) => {
  if (!brief) return null;
  
  return (
    <Paper
      elevation={0}
      sx={{ 
      p: 1,
      mb: 0,
      maxHeight: 125,
      overflow: 'hidden',
      backgroundImage: YOVO_CHAT_BODY_BG,
      backgroundColor: '#FFF5F8',
      borderRadius: YOVO_CHAT_RADIUS,
      border: '1px solid rgba(207, 32, 72, 0.12)',
      position: 'relative',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="subtitle2" sx={{ 
          fontWeight: 600, 
          color: YOVO_CHAT_ACCENT,
          fontSize: '0.7rem',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          lineHeight: 1.2,
        }}>
          <StyleIcon sx={{ fontSize: '13px' }} />
          Your Trip Brief
        </Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
            <CloseIcon sx={{ fontSize: '14px' }} />
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
        {brief.destination && (
          <Chip 
            icon={<LocationIcon sx={{ fontSize: '11px !important' }} />}
            label={brief.destination} 
            size="small" 
            sx={{ 
              bgcolor: YOVO_CHAT_ACCENT, 
              color: 'white',
              fontSize: '0.62rem',
              height: '20px',
              borderRadius: YOVO_CHAT_CHIP_RADIUS,
              '& .MuiChip-icon': { color: 'white' },
              '& .MuiChip-label': { px: 0.75 },
            }} 
          />
        )}
        {brief.dates && (
          <Chip 
            icon={<CalendarIcon sx={{ fontSize: '11px !important' }} />}
            label={brief.dates} 
            size="small" 
            sx={{ 
              bgcolor: '#495057', 
              color: 'white',
              fontSize: '0.62rem',
              height: '20px',
              borderRadius: YOVO_CHAT_CHIP_RADIUS,
              '& .MuiChip-icon': { color: 'white' },
              '& .MuiChip-label': { px: 0.75 },
            }} 
          />
        )}
        {brief.travellers && (
          <Chip 
            icon={<PeopleIcon sx={{ fontSize: '11px !important' }} />}
            label={brief.travellers} 
            size="small" 
            sx={{ 
              bgcolor: '#6c757d', 
              color: 'white',
              fontSize: '0.62rem',
              height: '20px',
              borderRadius: YOVO_CHAT_CHIP_RADIUS,
              '& .MuiChip-icon': { color: 'white' },
              '& .MuiChip-label': { px: 0.75 },
            }} 
          />
        )}
        {brief.trip_style && (
          <Chip 
            label={brief.trip_style} 
            size="small" 
            sx={{ 
              bgcolor: '#e9ecef', 
              color: '#495057',
              fontSize: '0.62rem',
              height: '20px',
              fontWeight: 500,
              borderRadius: YOVO_CHAT_CHIP_RADIUS,
              '& .MuiChip-label': { px: 0.75 },
            }} 
          />
        )}
      </Box>
      {brief.next_best_action && (
        <Typography variant="caption" sx={{ 
          display: 'block', 
          mt: 0.6, 
          color: '#6c757d',
          fontSize: '0.6rem',
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          Next: {brief.next_best_action}
        </Typography>
      )}
    </Paper>
  );
});

// Yovo Guide Badge Component
const YovoGuideBadge = memo(() => (
  <Box sx={{ 
    display: 'inline-flex', 
    alignItems: 'center', 
    gap: 0.3,
    bgcolor: '#e8f5e9',
    color: '#2e7d32',
    px: 0.75,
    py: 0.25,
    borderRadius: '4px',
    fontSize: '0.6rem',
    fontWeight: 600,
    mb: 0.5
  }}>
    <VerifiedIcon sx={{ fontSize: '10px' }} />
    Yovo Guide
  </Box>
));

// Plan Style Chips Component
const PlanStyleChips = memo(({ onSelect, selectedStyle }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
    {PLAN_STYLES.map((style) => (
      <Chip
        key={style.id}
        label={`${style.icon} ${style.label}`}
        size="small"
        onClick={() => onSelect(style)}
        sx={{
          bgcolor: selectedStyle?.id === style.id ? YOVO_CHAT_ACCENT : '#f5f5f5',
          color: selectedStyle?.id === style.id ? 'white' : '#333',
          fontSize: '0.65rem',
          height: '24px',
          cursor: 'pointer',
          borderRadius: YOVO_CHAT_CHIP_RADIUS,
          '&:hover': {
            bgcolor: selectedStyle?.id === style.id ? '#8a1f38' : '#e0e0e0',
          }
        }}
      />
    ))}
  </Box>
));

// Refinement Buttons Component
const RefinementButtons = memo(({ refinements, onSelect }) => {
  if (!refinements || refinements.length === 0) return null;
  
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
      {refinements.map((ref) => (
        <Button
          key={ref.id}
          size="small"
          variant="outlined"
          onClick={() => onSelect(ref)}
          sx={{
            fontSize: '0.65rem',
            textTransform: 'none',
            borderRadius: YOVO_CHAT_CHIP_RADIUS,
            px: 1.5,
            py: 0.25,
            minHeight: '24px',
            ...YOVO_CHAT_CTA_SX,
          }}
        >
          {ref.label}
        </Button>
      ))}
    </Box>
  );
});

// Animated Plane Component
const AnimatedPlane = ({ isVisible, onComplete }) => {
  const [animationPhase, setAnimationPhase] = useState(0);

  useEffect(() => {
    if (isVisible) {
      // Reset animation phase when becoming visible
      setAnimationPhase(0);
      
      const timer = setTimeout(() => {
        setAnimationPhase(1); // Plane draws and moves up to center
        setTimeout(() => {
          setAnimationPhase(2); // Plane reaches center and glows
          setTimeout(() => {
            setAnimationPhase(3); // Plane moves forward (left) continuously
            setTimeout(() => {
              setAnimationPhase(4); // Plane fades out with smooth transition
              setTimeout(() => {
                onComplete && onComplete();
              }, 800); // Increased fade time for smoother transition
            }, 1200); // Increased forward movement time
          }, 600); // Increased glow time
        }, 1200); // Increased drawing time
      }, 400); // Increased initial delay
      return () => clearTimeout(timer);
    } else {
      // Reset animation phase when not visible
      setAnimationPhase(0);
    }
  }, [isVisible, onComplete]);

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        pointerEvents: 'none',
        background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,1) 100%)',
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, rgba(171, 38, 69, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
            linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.02) 50%, transparent 70%)
          `,
          animation: 'backgroundShimmer 3s ease-in-out infinite',
          '@keyframes backgroundShimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' }
          }
        }}
      />

      {/* Floating Particles */}
      {[...Array(8)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: '3px',
            height: '3px',
            background: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '50%',
            top: `${20 + i * 10}%`,
            left: `${10 + i * 12}%`,
            animation: `particleFloat${i} ${2 + i * 0.3}s ease-in-out infinite`,
            [`@keyframes particleFloat${i}`]: {
              '0%, 100%': { 
                transform: 'translateY(0px) translateX(0px) scale(1)', 
                opacity: 0.3 
              },
              '50%': { 
                transform: `translateY(-${15 + i * 2}px) translateX(${5 + i}px) scale(1.2)`, 
                opacity: 0.8 
              }
            }
          }}
        />
      ))}

      {/* Main Plane Animation Container */}
      <Box
        sx={{
          position: 'relative',
          width: '100px',
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: animationPhase === 0 ? 'translateY(0px) translateX(0px) scale(0.3)' : 
                    animationPhase === 1 ? 'translateY(0px) translateX(0px) scale(1)' :
                    animationPhase === 2 ? 'translateY(0px) translateX(-20px) scale(1)' :
                    animationPhase === 3 ? 'translateY(0px) translateX(-100px) scale(1)' :
                    'translateY(0px) translateX(-200px) scale(1)',
          opacity: animationPhase === 0 ? 0 : 
                  animationPhase === 1 ? 1 :
                  animationPhase === 2 ? 1 : 
                  animationPhase === 3 ? 1 : 0,
          transition: animationPhase === 4 ? 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          filter: animationPhase === 2 ? 'drop-shadow(0 0 15px rgba(171, 38, 69, 0.6))' : 'none',
        }}
      >
        {/* Plane SVG */}
        <svg
          width="70"
          height="50"
          viewBox="0 0 80 60"
          style={{
            transform: 'rotate(0deg)',
            transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
        >
          {/* Plane Body */}
          <path
            d="M15 30 L65 30 L70 35 L65 40 L15 40 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '200',
              strokeDashoffset: animationPhase === 0 ? '200' : '0',
              transition: 'stroke-dashoffset 1.5s ease-in-out',
            }}
          />
          
          {/* Plane Wings */}
          <path
            d="M25 30 L35 20 L45 20 L35 30 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '100',
              strokeDashoffset: animationPhase === 0 ? '100' : '0',
              transition: 'stroke-dashoffset 1.2s ease-in-out 0.3s',
            }}
          />
          
          <path
            d="M25 40 L35 50 L45 50 L35 40 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '100',
              strokeDashoffset: animationPhase === 0 ? '100' : '0',
              transition: 'stroke-dashoffset 1.2s ease-in-out 0.3s',
            }}
          />
          
          {/* Plane Nose */}
          <path
            d="M15 30 L5 35 L15 40 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '50',
              strokeDashoffset: animationPhase === 0 ? '50' : '0',
              transition: 'stroke-dashoffset 1s ease-in-out 0.6s',
            }}
          />
          
          {/* Plane Tail */}
          <path
            d="M65 30 L75 25 L75 35 L65 40 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '60',
              strokeDashoffset: animationPhase === 0 ? '60' : '0',
              transition: 'stroke-dashoffset 1s ease-in-out 0.9s',
            }}
          />
          
          {/* Propeller */}
          <circle
            cx="15"
            cy="35"
            r="3"
            fill="none"
            stroke={YOVO_CHAT_ACCENT}
            strokeWidth="1.5"
            style={{
              strokeDasharray: '20',
              strokeDashoffset: animationPhase === 0 ? '20' : '0',
              transition: 'stroke-dashoffset 0.8s ease-in-out 1.2s',
            }}
          />
          
          {/* Propeller Blades */}
          <path
            d="M15 35 L20 35 M15 35 L10 35 M15 35 L15 30 M15 35 L15 40"
            stroke={YOVO_CHAT_ACCENT}
            strokeWidth="1"
            strokeLinecap="round"
            style={{
              strokeDasharray: '40',
              strokeDashoffset: animationPhase === 0 ? '40' : '0',
              transition: 'stroke-dashoffset 0.8s ease-in-out 1.2s',
              animation: animationPhase >= 1 ? 'propellerSpin 0.1s linear infinite' : 'none',
            }}
          />
        </svg>


        {/* Speed Trail Effect - Only shows during forward movement */}
        {animationPhase >= 3 && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '120px',
              height: '4px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(171, 38, 69, 0.6) 30%, rgba(255, 255, 255, 0.4) 70%, transparent 100%)',
              borderRadius: '2px',
              animation: 'speedTrail 0.5s ease-out infinite',
              '@keyframes speedTrail': {
                '0%': { 
                  transform: 'translate(-50%, -50%) scaleX(0)',
                  opacity: 0 
                },
                '50%': { 
                  transform: 'translate(-50%, -50%) scaleX(1)',
                  opacity: 0.8 
                },
                '100%': { 
                  transform: 'translate(-50%, -50%) scaleX(1.2)',
                  opacity: 0 
                }
              }
            }}
          />
        )}

        {/* Propeller Spin Animation */}
        <style>
          {`
            @keyframes propellerSpin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </Box>

    </Box>
  );
};

const Chatbot = memo(({ open, setOpen, flightData = null, flushBottom = false }) => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const handleSendRef = useRef(null);
  
  // Trip planning state
  const [currentTripBrief, setCurrentTripBrief] = useState(null);
  const [selectedPlanStyle, setSelectedPlanStyle] = useState(null);
  
  // Off-topic tracking for intent classification
  const [offTopicCount, setOffTopicCount] = useState(0);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
  /** First-open: homepage lady mascot, then welcome text */
  const [showLadyIntro, setShowLadyIntro] = useState(false);
  
  // Opening animation state
  // const [showOpeningAnimation, setShowOpeningAnimation] = useState(false);
  // const [animationComplete, setAnimationComplete] = useState(false);

  // Interactive form states (trip type / city only — no date CTA)
  const [showInteractiveForm, setShowInteractiveForm] = useState(false);
  const [interactiveFormType, setInteractiveFormType] = useState('');
  const [tripType, setTripType] = useState('oneway');
  const [departureCity, setDepartureCity] = useState(null);
  const [departureCityOptions, setDepartureCityOptions] = useState([]);
  const [departureCityLoading, setDepartureCityLoading] = useState(false);

  // Storage keys for chat persistence
  const CHAT_STORAGE_KEY = 'yovo_chatbot_messages';
  const CONVERSATION_STORAGE_KEY = 'yovo_chatbot_conversation';
  const CHAT_TIMESTAMP_KEY = 'yovo_chatbot_last_interaction';
  const CHAT_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours
  const LADY_INTRO_KEY = 'yovo_assist_lady_intro_seen';

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Empty / first-open: welcome hero + CTAs only (no user turns yet)
  const showWelcomeQuickActions = useMemo(() => {
    if (isLoading) return false;
    return !messages.some((m) => m.from === 'user' || m.isStreaming);
  }, [messages, isLoading]);

  // Keep lady + gradient welcome bubble consistent after first send
  const showWelcomeHero = useMemo(() => {
    if (!messages.length) return true;
    return messages.some(isWelcomeBotMessage);
  }, [messages]);

  // Conversation turns only — welcome stays in hero, not plain text bubble
  const conversationMessages = useMemo(
    () => messages.filter((m) => !isWelcomeBotMessage(m)),
    [messages]
  );

  // Skip sequential lady-only splash — Figma shows lady + welcome bubble together
  useEffect(() => {
    if (showLadyIntro) setShowLadyIntro(false);
  }, [showLadyIntro]);

  // Trigger opening animation when chatbot opens
  // useEffect(() => {
  //   if (open && !animationComplete) {
  //     setShowOpeningAnimation(true);
  //   } else if (!open) {
  //     setShowOpeningAnimation(false);
  //     setAnimationComplete(false);
  //   }
  // }, [open, animationComplete]);

  // Handle animation completion
  // const handleAnimationComplete = useCallback(() => {
  //   setAnimationComplete(true);
  //   // Delay hiding the opening animation to allow smooth transition
  //   setTimeout(() => {
  //     setShowOpeningAnimation(false);
  //   }, 200);
  //   // Scroll to bottom when chat window appears
  //   setTimeout(() => {
  //     scrollToBottom();
  //   }, 300);
  // }, [scrollToBottom]);

  // Memoize message rendering for better performance
  const renderedMessages = useMemo(() => {
    return conversationMessages.map((msg, idx) => {
      const isUser = msg.from === 'user';
      return (
      <Box key={idx} sx={{ mb: 1.75, display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
        <Box sx={{ 
          ...(isUser
            ? { px: 1.25, py: 1 }
            : { p: 0.25 }),
          bgcolor: isUser ? YOVO_CHAT_ACCENT : 'transparent',
          color: isUser ? 'white' : '#0D0D0D',
          borderRadius: isUser ? YOVO_CHAT_RADIUS : 0,
          maxWidth: isUser ? '85%' : '100%',
          position: 'relative',
          boxShadow: 'none',
          border: 'none',
        }}>
          <Typography variant="body2" sx={{ 
            whiteSpace: 'pre-line', 
            lineHeight: 1.45,
            fontFamily: '"Red Hat Display", -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: isUser ? 'white' : '#0D0D0D',
            textAlign: 'left',
            display: 'block',
          }}>
            {msg.isStreaming && !String(msg.text || '').trim() ? (
              <YovoWorkingIndicator />
            ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%',
              }}
            >
              {expandMessageLines(msg.text).map((line, index) => {
              // Hide flag lines completely
              if (line.includes('[FLAG:')) {
                return null;
              }
              
              // DISABLED: Legacy markdown-style train link parsing
              // Structured actions from backend are now the only trusted path
              const trainMarkdownLinkMatch = null; // Disabled for safety
              if (false /* trainMarkdownLinkMatch && msg.from === 'bot' - DISABLED */) {
                return null;
              }
              
              // DISABLED: Legacy markdown-style hotel link parsing
              // Structured actions from backend are now the only trusted path
              const hotelMarkdownLinkMatch = null; // Disabled for safety
              if (false /* hotelMarkdownLinkMatch && msg.from === 'bot' - DISABLED */) {
                return null;
              }
              
              // DISABLED: Legacy markdown-style flight link parsing
              // Structured actions from backend are now the only trusted path
              const flightMarkdownLinkMatch = null; // Disabled for safety
              if (false /* flightMarkdownLinkMatch && msg.from === 'bot' - DISABLED */) {
                return null;
              }
              
              // DISABLED: Legacy regex-based flight button parsing
              // Structured actions from backend are now the only trusted path
              // Book Flight buttons are rendered via msg.actions from backend
              const bookFlightMatch = null; // Disabled: line.match(/\[Book Flight to ([^\]]+)\]/)
              const bookMulticityMatch = null; // Disabled: line.match(/\[Book Multicity Flight\]/)
              
              if (false /* bookFlightMatch - DISABLED for safety */) {
                return null;
              }
              
              // DISABLED: Legacy multicity flight button - use structured actions instead
              if (false /* bookMulticityMatch - DISABLED for safety */) {
                return null;
              }
              
              // DISABLED: Legacy regex-based train URL parsing
              // Structured actions from backend are now the only trusted path
              // Train buttons are rendered via msg.actions from backend
              let trainUrl = null; // Disabled for safety
              
              if (false /* trainUrl && msg.from === 'bot' - DISABLED for safety */) {
                return null;
              }
              
              // DISABLED: Legacy regex-based hotel URL parsing
              // Structured actions from backend are now the only trusted path
              // Hotel buttons are rendered via msg.actions from backend
              let hotelUrl = null; // Disabled for safety
              
              if (false /* hotelUrl && msg.from === 'bot' - DISABLED for safety */) {
                return null;
              }
              
              // DISABLED: Legacy regex-based standalone flight URL parsing
              // Structured actions from backend are now the only trusted path
              // Flight buttons are rendered via msg.actions from backend
              const flightUrlMatch = null; // Disabled for safety
              if (false /* flightUrlMatch && msg.from === 'bot' - DISABLED for safety */) {
                return null;
              }
              
              // Check for lines that contain only the flight URL (to hide them)
              // This handles both simple URLs and complex URLs with userLocation parameter
              // Note: This is now a fallback since we create buttons above
              if (line.trim().match(/^\/?flight\?[^\s]*$/) && msg.from === 'bot') {
                // This case is already handled above, but if somehow we get here, hide it
                return null;
              }

              // Also hide lines that contain flight URLs with userLocation parameter (multiline URLs)
              if (line.trim().match(/^\/?flight\?.*userLocation.*$/)) {
                return null; // Hide the URL line completely
              }

              // Hide lines that start with flight? and contain complex parameters
              if (line.trim().match(/^\/?flight\?.*&.*$/)) {
                return null; // Hide the URL line completely
              }
              
              // Hide lines that contain only train URLs (to hide them) - this is already handled above by creating a button
              // Check for train URL patterns that should be hidden (already converted to buttons above)
              if (msg.from === 'bot') {
                const trainUrlPattern1 = line.trim().match(/^\/?train\?[^\s]*$/);
                const trainUrlPattern2 = line.trim().match(/^from=[^&\s]+&to=[^&\s]+.*journeyDate/);
                const trainUrlPattern3 = line.trim().includes('from=') && 
                                        line.trim().includes('to=') && 
                                        line.trim().includes('journeyDate') &&
                                        !line.trim().includes('[') && // Not a markdown link
                                        !line.trim().includes(']('); // Not a markdown link
                if (trainUrlPattern1 || trainUrlPattern2 || trainUrlPattern3) {
                  // This case is already handled above by creating a button, so hide the raw URL
                  return null;
                }
                
                // Hide hotel URL patterns (already converted to buttons above)
                const hotelUrlPattern1 = line.trim().match(/^\/?hotel\?[^\s]*$/);
                const hotelUrlPattern2 = line.trim().includes('location=') && 
                                        line.trim().includes('checkIn=') && 
                                        line.trim().includes('checkOut=') &&
                                        !line.trim().includes('[') && // Not a markdown link
                                        !line.trim().includes(']('); // Not a markdown link
                if (hotelUrlPattern1 || hotelUrlPattern2) {
                  // This case is already handled above by creating a button, so hide the raw URL
                  return null;
                }
              }
              
              // Format bullet points (do not treat **bold** day headers as bullets)
              const trimmedLine = line.trim();
              const isBulletLine =
                trimmedLine.startsWith('•') ||
                (trimmedLine.startsWith('-') && !trimmedLine.startsWith('--')) ||
                (trimmedLine.startsWith('*') && !trimmedLine.startsWith('**'));
              if (isBulletLine) {
                return (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    pl: 1
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: isUser ? 'rgba(255,255,255,0.9)' : YOVO_CHAT_ACCENT, 
                      fontWeight: 600, 
                      mr: 1,
                      fontSize: '16px',
                      lineHeight: 1.2
                    }}>
                      •
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      flex: 1,
                      fontSize: '14px',
                      lineHeight: 1.4,
                      color: isUser ? 'white' : '#0D0D0D'
                    }}>
                      {renderInlineMarkdown(trimmedLine.replace(/^[•\-*]\s*/, ''))}
                    </Typography>
                  </Box>
                );
              }
              
              // Format numbered lists (skip bare "1." with no text)
              if (trimmedLine.match(/^\d+\./)) {
                const number = trimmedLine.match(/^(\d+)\./)[1];
                const text = trimmedLine.substring(number.length + 1).trim();
                if (!text) return null;
                return (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    pl: 1
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: isUser ? 'rgba(255,255,255,0.9)' : YOVO_CHAT_ACCENT, 
                      fontWeight: 600, 
                      mr: 1,
                      fontSize: '14px',
                      lineHeight: 1.2,
                      minWidth: '20px'
                    }}>
                      {number}.
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      flex: 1,
                      fontSize: '14px',
                      lineHeight: 1.4,
                      color: isUser ? 'white' : '#0D0D0D'
                    }}>
                      {renderInlineMarkdown(text)}
                    </Typography>
                  </Box>
                );
              }
              // Regular text (with inline **bold**) — spacing via parent gap, not mb
              return trimmedLine ? (
                <Typography key={index} variant="body2" component="span" sx={{ display: 'block', m: 0 }}>
                  {renderInlineMarkdown(line)}
                </Typography>
              ) : null;
            })}
            </Box>
            )}
          </Typography>
          {/* Yovo Guide badge for internal data */}
          {msg.source === 'yovo_guide' && msg.from === 'bot' && (
            <YovoGuideBadge />
          )}
          
          {/* Trip Brief lives in sticky header only — avoid duplicate card in bubble */}
          
          {/* Render structured action buttons from backend */}
          {msg.actions && msg.actions.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {msg.actions.map((action, actionIdx) => (
                <Button
                  key={actionIdx}
                  variant="contained"
                  disableElevation
                  size="small"
                  startIcon={
                    action.type === 'flight_search' ? <FlightIcon sx={{ fontSize: '14px' }} /> :
                    action.type === 'hotel_search' ? <HotelIcon sx={{ fontSize: '14px' }} /> :
                    action.type === 'train_search' ? <TrainIcon sx={{ fontSize: '14px' }} /> :
                    action.type === 'email_support' ? <EmailIcon sx={{ fontSize: '14px' }} /> :
                    action.type === 'external_link' ? <ExternalLinkIcon sx={{ fontSize: '14px' }} /> : null
                  }
                  onClick={() => {
                    if (action.type === 'email_support' && (action.mailto || action.url)) {
                      openEmailSupportAction(action);
                      return;
                    }
                    if (action.type === 'external_link' && /^https?:\/\//i.test(action.url || '')) {
                      window.open(action.url, '_blank', 'noopener,noreferrer');
                      return;
                    }
                    console.log(`Navigating to ${action.type}:`, action.url);
                    window.location.href = action.url;
                  }}
                  sx={{
                    ...YOVO_CHAT_CTA_SX,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: YOVO_CHAT_CHIP_RADIUS,
                    px: 2,
                    py: 0.75,
                    '& .MuiButton-startIcon': { color: YOVO_CHAT_ACCENT },
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
          
          {/* Refinement buttons */}
          {msg.refinements && msg.refinements.length > 0 && msg.from === 'bot' && (
            <RefinementButtons 
              refinements={msg.refinements} 
              onSelect={(ref) => {
                // Send refinement request as a new message using the ref
                if (handleSendRef.current) {
                  handleSendRef.current(ref.label);
                }
              }}
            />
          )}
          
          {/* Suggested action chips (from intent classification) */}
          {msg.suggestedActions && msg.suggestedActions.length > 0 && msg.from === 'bot' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
              {msg.suggestedActions.map((action) => (
                <Chip
                  key={action.id}
                  label={action.label}
                  size="small"
                  onClick={() => {
                    if (action.type === 'email_support' || action.id === 'email_support') {
                      openEmailSupportAction(action);
                      return;
                    }
                    // Send the action's prompt as a new message
                    if (handleSendRef.current && action.prompt) {
                      handleSendRef.current(action.prompt);
                    }
                  }}
                  sx={{
                    ...YOVO_CHAT_CTA_SX,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    borderRadius: YOVO_CHAT_CHIP_RADIUS,
                  }}
                />
              ))}
            </Box>
          )}
      </Box>
      </Box>
    );
    });
  }, [conversationMessages, navigate]);

  // Check for interactive form flags in messages
  const checkForInteractiveFlags = (messageText) => {
    const text = String(messageText || '');

    if (text.includes('[FLAG:TRIP_TYPE_SELECTION]')) {
      setInteractiveFormType('trip_type');
      setShowInteractiveForm(true);
      return true;
    }
    if (text.includes('[FLAG:DEPARTURE_CITY_SELECTION]')) {
      setInteractiveFormType('departure_city');
      setShowInteractiveForm(true);
      return true;
    }
    return false;
  };

  // Load departure city options from real API
  const loadDepartureCityOptions = async (query) => {
    if (!query || query.length < 2) return;
    
    setDepartureCityLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/airports/search?q=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();
      
      if (data.success && data.data) {
        const airports = data.data.map(airport => ({
          code: airport.iataCode,
          name: `${airport.address?.cityName || airport.name} (${airport.iataCode})`,
          city: airport.address?.cityName || '',
          country: airport.address?.countryName || ''
        }));
        setDepartureCityOptions(airports);
      } else {
        setDepartureCityOptions([]);
      }
    } catch (error) {
      console.error('Error loading departure cities:', error);
      setDepartureCityOptions([]);
    } finally {
      setDepartureCityLoading(false);
    }
  };

  // Load chat history from localStorage on component mount (with TTL check)
  useEffect(() => {
    try {
      // Check TTL - clear stale history
      const lastInteraction = localStorage.getItem(CHAT_TIMESTAMP_KEY);
      const isStale = lastInteraction && (Date.now() - parseInt(lastInteraction, 10) > CHAT_TTL_MS);
      
      if (isStale) {
        localStorage.removeItem(CHAT_STORAGE_KEY);
        localStorage.removeItem(CONVERSATION_STORAGE_KEY);
        localStorage.removeItem(CHAT_TIMESTAMP_KEY);
        // Also clear floating chatbot storage for consistency
        localStorage.removeItem('yovo_floating_chatbot_messages');
        localStorage.removeItem('yovo_floating_chatbot_conversation');
      }
      
      const savedMessages = localStorage.getItem(CHAT_STORAGE_KEY);
      const savedConversation = localStorage.getItem(CONVERSATION_STORAGE_KEY);
      
      if (savedMessages && !isStale) {
        const parsedMessages = JSON.parse(savedMessages);
        // Migrate cached greeting → current Yovo Assist welcome (keep hero consistent)
        if (Array.isArray(parsedMessages)) {
          parsedMessages.forEach((m, i) => {
            if (m?.from === 'bot' && (isLegacyWelcomeText(m?.text) || isYovoAssistWelcomeText(m?.text))) {
              parsedMessages[i] = { ...m, text: YOVO_ASSIST_WELCOME };
            }
          });
        }
        setMessages(parsedMessages);
      }
      
      if (savedConversation && !isStale) {
        const parsedConversation = JSON.parse(savedConversation);
        setConversationHistory(parsedConversation);
      }
      
      // Check if there's an initial message from homepage (regardless of saved messages)
      if (window.chatbotInitialMessage) {
        setTimeout(() => {
          handleSend(window.chatbotInitialMessage);
          delete window.chatbotInitialMessage;
        }, 1000);
      }
      
      // If no saved messages or stale, show welcome message
      if (!savedMessages || isStale || JSON.parse(savedMessages || '[]').length === 0) {
        const welcomeMessage = {
          from: 'bot',
          text: YOVO_ASSIST_WELCOME,
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
        // Lady + welcome bubble render together in empty-state hero (no timed splash)
        try {
          sessionStorage.setItem(LADY_INTRO_KEY, '1');
        } catch {
          /* ignore */
        }
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      const welcomeMessage = {
        from: 'bot',
        text: YOVO_ASSIST_WELCOME,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, []); // Only run once on mount

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
        localStorage.setItem(CHAT_TIMESTAMP_KEY, Date.now().toString());
      } catch (error) {
        console.error('Error saving messages to localStorage:', error);
      }
    }
  }, [messages]);

  // Save conversation history to localStorage whenever it changes
  useEffect(() => {
    if (conversationHistory.length > 0) {
      try {
        localStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(conversationHistory));
        localStorage.setItem(CHAT_TIMESTAMP_KEY, Date.now().toString());
      } catch (error) {
        console.error('Error saving conversation history to localStorage:', error);
      }
    }
  }, [conversationHistory]);

  // No longer needed - welcome message is handled in the load effect


  const handleSend = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = {
      from: 'user',
      text: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(false); // We'll handle loading state with streaming

    // Create initial bot message for streaming
    const botMessage = {
      from: 'bot',
      text: '',
      timestamp: new Date(),
      error: null,
      isStreaming: true
    };

    setMessages(prev => [...prev, botMessage]);

    try {
      // Prepare conversation history for context
      // Filter out system/quota messages and keep last 20 exchanges for longer context
      const historyForApi = conversationHistory
        .filter(msg => !msg.isSystemMessage)
        .slice(-20);
      
      let fullResponse = '';
      
      const response = await chatbotService.sendMessageStream(
        messageText, 
        historyForApi,
        (chunk) => {
          // Server sends full buffered content snapshots — replace, don't append
          fullResponse = chunk;
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage.from === 'bot' && lastMessage.isStreaming) {
              lastMessage.text = fullResponse;
            }
            return newMessages;
          });
        },
        offTopicCount
      );
      
      // Update off-topic counter from response
      if (typeof response.offTopicCount === 'number') {
        setOffTopicCount(response.offTopicCount);
      }
      
      // Mark streaming as complete and add actions, tripBrief, refinements, suggestedActions
      const rawText = response?.message != null ? String(response.message) : '';
      const cleaned = stripLeakedStructuredMarkers(rawText);
      const hasActions = Array.isArray(response?.actions) && response.actions.length > 0;
      const emptyReply = !(cleaned && cleaned.trim()) && !hasActions;
      const displayText = emptyReply
        ? (fallbackResponses.empty || fallbackResponses.error)
        : (cleaned && cleaned.trim()
          ? cleaned
          : (hasActions ? 'Here’s what I found — tap below to continue.' : fallbackResponses.empty));

      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.from === 'bot' && lastMessage.isStreaming) {
          lastMessage.isStreaming = false;
          lastMessage.text = displayText;
          lastMessage.error = (!response.success || emptyReply) ? (response.error || 'EMPTY_RESPONSE') : null;
          lastMessage.actions = response.actions || [];
          lastMessage.suggestedActions = response.suggestedActions || null;
          lastMessage.tripBrief = response.tripBrief || null;
          lastMessage.refinements = response.refinements || null;
          lastMessage.source = response.source || null;
        }
        return newMessages;
      });
      
      // Update current trip brief if present
      if (response.tripBrief) {
        setCurrentTripBrief(response.tripBrief);
      }
      
      // Check for interactive form flags
      if (response.success && !emptyReply) {
        checkForInteractiveFlags(response.message);
      }
      
      // Update conversation history with what the user actually saw
      // Mark throttle/system messages so they're excluded from API context
      const isThrottleMessage = response.source === 'throttle';
      setConversationHistory(prev => [
        ...prev,
        { role: 'user', content: messageText, isSystemMessage: isThrottleMessage },
        { role: 'assistant', content: displayText, isSystemMessage: isThrottleMessage }
      ]);

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Update the streaming message to show error
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.from === 'bot' && lastMessage.isStreaming) {
          lastMessage.isStreaming = false;
          lastMessage.text = fallbackResponses.error;
          lastMessage.error = true;
        }
        return newMessages;
      });
    }
  };
  
  // Store handleSend in ref for child components (refinement buttons)
  handleSendRef.current = handleSend;
  
  // Handle plan style selection
  const handlePlanStyleSelect = (style) => {
    setSelectedPlanStyle(style);
    handleSend(`I prefer a ${style.label.toLowerCase()} trip`);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Parse travel guide responses and extract flight booking information
  const parseTravelGuideResponse = (userMessage, aiResponse) => {
    const destinations = [];
    let departureCity = '';
    let travelDate = '';
    let destination = '';
    let showFlightBooking = false;

    // Check if this is a flight booking request
    const bookingPatterns = [
      /book.*flight/i,
      /yes.*book/i,
      /book.*ticket/i,
      /find.*flight/i,
      /search.*flight/i
    ];

    const isBookingRequest = bookingPatterns.some(pattern => 
      pattern.test(userMessage) || pattern.test(aiResponse)
    );

    // Extract departure city from various patterns
    const cityPatterns = [
      /from\s+([a-zA-Z\s]+?)(?:\s+on|\s+to|\s*$)/i,
      /flying\s+from\s+([a-zA-Z\s]+)/i,
      /departure\s+(?:city|from)\s*:?\s*([a-zA-Z\s]+)/i,
      /leaving\s+from\s+([a-zA-Z\s]+)/i,
      /starting\s+from\s+([a-zA-Z\s]+)/i
    ];

    // Check current message for departure city
    for (const pattern of cityPatterns) {
      const cityMatch = userMessage.match(pattern);
      if (cityMatch) {
        departureCity = cityMatch[1].trim();
        break;
      }
    }

    // Check conversation history for departure city
    if (!departureCity) {
      const recentMessages = conversationHistory.slice(-5);
      for (let i = recentMessages.length - 1; i >= 0; i--) {
        const message = recentMessages[i].content;
        for (const pattern of cityPatterns) {
          const cityMatch = message.match(pattern);
          if (cityMatch) {
            departureCity = cityMatch[1].trim();
            break;
          }
        }
        if (departureCity) break;
      }
    }

    // Extract travel dates
    const datePatterns = [
      /on\s+(\d{1,2}(?:st|nd|rd|th)?\s+(?:january|february|march|april|may|june|july|august|september|october|november|december))/i,
      /on\s+(\d{1,2}\/\d{1,2}\/\d{4})/,
      /on\s+(\d{4}-\d{2}-\d{2})/,
      /(\d{1,2}(?:st|nd|rd|th)?\s+(?:january|february|march|april|may|june|july|august|september|october|november|december))/i,
      /date\s*:?\s*(\d{1,2}[\/-]\d{1,2}[\/-]\d{4})/i,
      /(tomorrow|today|next week|next month)/i
    ];

    for (const pattern of datePatterns) {
      const dateMatch = userMessage.match(pattern);
      if (dateMatch) {
        travelDate = dateMatch[1];
        break;
      }
    }

    // Check conversation history for dates
    if (!travelDate) {
      const recentMessages = conversationHistory.slice(-5);
      for (let i = recentMessages.length - 1; i >= 0; i--) {
        const message = recentMessages[i].content;
        for (const pattern of datePatterns) {
          const dateMatch = message.match(pattern);
          if (dateMatch) {
            travelDate = dateMatch[1];
            break;
          }
        }
        if (travelDate) break;
      }
    }

    // Extract destination from AI response
    const destinationPatterns = [
      /to\s+([A-Za-z\s]+?)(?:\s+\(|\s+for|\s*$)/i,
      /([A-Za-z\s]+?)\s*\(([A-Z]{3})\)/,
      /destination.*?([A-Za-z\s]+)/i
    ];

    for (const pattern of destinationPatterns) {
      const destMatch = aiResponse.match(pattern);
      if (destMatch) {
        destination = destMatch[1] || destMatch[2];
        break;
      }
    }

    // Check if AI response indicates flight booking should be shown
    const bookingIndicators = [
      /book.*flight/i,
      /flight.*booking/i,
      /find.*flight/i,
      /search.*flight/i,
      /Book Flight to/i
    ];

    showFlightBooking = bookingIndicators.some(pattern => pattern.test(aiResponse));

    // If we have a destination and booking is requested, create destination info
    if (destination && (isBookingRequest || showFlightBooking)) {
      // Map common destinations to airport codes
      const destinationAirports = {
        'thailand': { code: 'BKK', city: 'Bangkok' },
        'bangkok': { code: 'BKK', city: 'Bangkok' },
        'phuket': { code: 'HKT', city: 'Phuket' },
        'singapore': { code: 'SIN', city: 'Singapore' },
        'dubai': { code: 'DXB', city: 'Dubai' },
        'paris': { code: 'CDG', city: 'Paris' },
        'london': { code: 'LHR', city: 'London' },
        'tokyo': { code: 'NRT', city: 'Tokyo' },
        'new york': { code: 'JFK', city: 'New York' },
        'bali': { code: 'DPS', city: 'Bali' },
        'goa': { code: 'GOI', city: 'Goa' },
        'mumbai': { code: 'BOM', city: 'Mumbai' },
        'delhi': { code: 'DEL', city: 'Delhi' }
      };

      const destKey = destination.toLowerCase();
      const airportInfo = destinationAirports[destKey] || { code: 'XXX', city: destination };
        
        destinations.push({
        city: airportInfo.city,
        code: airportInfo.code,
        label: `${airportInfo.city} (${airportInfo.code})`,
        description: `Amazing destination with rich culture and experiences`
      });
    }

    return {
      departureCity,
      travelDate,
      destinations,
      destination,
      showFlightBooking
    };
  };

  // DISABLED: Legacy handleFlightSearch with hardcoded cityToCode
  // All flight searches now go through structured actions from backend
  // eslint-disable-next-line no-unused-vars
  const handleFlightSearch = (fromCity, toCode, date) => {
    // This function is disabled - all flight navigation should use structured actions
    console.warn('handleFlightSearch is disabled. Use structured actions from backend.');
    // Do not navigate - this prevents any accidental use of hardcoded codes
  };

  // Clear chat history (including sibling widgets for consistency)
  const clearChatHistory = () => {
    try {
      localStorage.removeItem(CHAT_STORAGE_KEY);
      localStorage.removeItem(CONVERSATION_STORAGE_KEY);
      localStorage.removeItem(CHAT_TIMESTAMP_KEY);
      // Also clear floating chatbot storage for consistency
      localStorage.removeItem('yovo_floating_chatbot_messages');
      localStorage.removeItem('yovo_floating_chatbot_conversation');
      // Clear chatbot session ID to reset quotas
      clearChatbotSession();
      
      // Reset to welcome message
      const welcomeMessage = {
        from: 'bot',
        text: YOVO_ASSIST_WELCOME,
        timestamp: new Date()
      };
      
      setMessages([welcomeMessage]);
      setConversationHistory([]);
      setHeaderMenuAnchor(null);
      setCurrentTripBrief(null);
      setShowInteractiveForm(false);
      setInteractiveFormType('');
      
    } catch (error) {
      console.error('Error clearing chat history:', error);
    }
  };

  if (!open) return null;

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      // One continuous canvas behind header (opaque) + messages + input
      background: YOVO_CHAT_BODY_BG,
      position: 'relative',
      fontFamily: '"Red Hat Display", -apple-system, Roboto, Helvetica, sans-serif',
      borderRadius: flushBottom ? `${YOVO_CHAT_SHELL_RADIUS} ${YOVO_CHAT_SHELL_RADIUS} 0 0` : YOVO_CHAT_SHELL_RADIUS,
      overflow: 'hidden',
      // Epic entrance animation sequence - starts earlier for smoother transition
      // animation: animationComplete ? 'chatbotEpicEntrance 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s both' : 'none',
      '@keyframes chatbotEpicEntrance': {
        '0%': {
          transform: 'translateY(120px) scale(0.4) rotateX(30deg)',
          opacity: 0,
          filter: 'blur(8px)',
        },
        '15%': {
          transform: 'translateY(60px) scale(0.7) rotateX(15deg)',
          opacity: 0.2,
          filter: 'blur(4px)',
        },
        '30%': {
          transform: 'translateY(20px) scale(0.9) rotateX(5deg)',
          opacity: 0.5,
          filter: 'blur(2px)',
        },
        '50%': {
          transform: 'translateY(-10px) scale(1.02) rotateX(-2deg)',
          opacity: 0.8,
          filter: 'blur(1px)',
        },
        '70%': {
          transform: 'translateY(5px) scale(0.99) rotateX(1deg)',
          opacity: 0.95,
          filter: 'blur(0px)',
        },
        '85%': {
          transform: 'translateY(-2px) scale(1.005) rotateX(-0.5deg)',
          opacity: 0.98,
        },
        '100%': {
          transform: 'translateY(0) scale(1) rotateX(0deg)',
          opacity: 1,
        }
      }
    }}>
      {/* Opening Animation Overlay */}
      {/* <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          opacity: showOpeningAnimation ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: showOpeningAnimation ? 'none' : 'none',
        }}
      >
        <AnimatedPlane 
          key={showOpeningAnimation ? 'animation-active' : 'animation-inactive'}
          isVisible={showOpeningAnimation} 
          onComplete={handleAnimationComplete}
        />
      </Box> */}
      
      {/* Chatbot Content - Only show after animation completes */}
      <Box
        sx={{
          // opacity: animationComplete ? 1 : 0,
          // transition: 'opacity 0.5s ease-in-out 0.2s',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* {animationComplete && ( */}
          <>
          {/* Header Section — premium dark + Yovo pink (header-only redesign) */}
          <Box
            sx={{
        flexShrink: 0,
              minHeight: 68,
              maxHeight: 72,
              px: 1.5,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              borderTopLeftRadius: YOVO_CHAT_SHELL_RADIUS,
              borderTopRightRadius: YOVO_CHAT_SHELL_RADIUS,
        position: 'relative',
              overflow: 'hidden',
              background: `
                radial-gradient(ellipse 80% 120% at 100% 100%, rgba(233, 30, 99, 0.14) 0%, transparent 55%),
                linear-gradient(165deg, #0B0B0D 0%, #0F0F11 45%, #121214 100%)
              `,
              borderBottom: '1px solid rgba(207, 32, 72, 0.35)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
            }}
          >
            {/* Subtle flight path + plane (decorative, non-interactive) */}
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                zIndex: 0,
                opacity: 0.16,
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 360 72"
                preserveAspectRatio="none"
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
              >
                <path
                  d="M40 58 C 110 52, 160 18, 220 22 S 300 48, 340 14"
                  fill="none"
                  stroke={YOVO_CHAT_ACCENT}
                  strokeWidth="1.2"
                  strokeDasharray="3 5"
                  strokeLinecap="round"
                />
              </Box>
              <FlightIcon
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 72,
                  fontSize: 14,
                  color: YOVO_CHAT_ACCENT,
                  transform: 'rotate(-18deg)',
                }}
              />
            </Box>

            <Box
              sx={{
            display: 'flex',
            alignItems: 'center',
                gap: 1.25,
                width: '100%',
                minWidth: 0,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box
                component="img"
                src={yovoAssistIcon}
                alt="Yovo Assist"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '11px',
                  objectFit: 'cover',
                  flexShrink: 0,
                  boxShadow: `0 0 14px rgba(207, 32, 72, 0.45), 0 2px 8px rgba(0,0,0,0.35)`,
                }}
              />

              <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Typography
                    component="span"
                    sx={{
                      color: '#fff',
                      fontSize: { xs: '1rem', sm: '1.0625rem' },
                      fontWeight: 700,
                      letterSpacing: '0.01em',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Yovo Assist
                  </Typography>
          </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.62)',
                      fontSize: '0.71875rem',
                      fontWeight: 400,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Your AI travel companion
            </Typography>
                  <AutoAwesome
                    sx={{
                      fontSize: 11,
                      color: 'rgba(233, 30, 99, 0.75)',
                      flexShrink: 0,
                      opacity: 0.9,
                    }}
                    aria-hidden
                  />
          </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
            <IconButton 
                  aria-label="More options"
                  aria-haspopup="menu"
                  aria-controls={headerMenuAnchor ? 'yovo-assist-header-menu' : undefined}
                  aria-expanded={Boolean(headerMenuAnchor)}
                  onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
              sx={{ 
                    width: 34,
                    height: 34,
                    borderRadius: '11px',
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(207, 32, 72, 0.35)',
                '&:hover': { 
                      bgcolor: 'rgba(207, 32, 72, 0.18)',
                      borderColor: 'rgba(207, 32, 72, 0.55)',
                      boxShadow: '0 0 12px rgba(207, 32, 72, 0.25)',
                },
              }}
            >
                  <MoreHorizIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton 
                  aria-label="Minimize chat"
                  title="Minimize"
              onClick={() => setOpen(false)} 
              sx={{ 
                    width: 34,
                    height: 34,
                    borderRadius: '11px',
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                '&:hover': { 
                      bgcolor: 'rgba(207, 32, 72, 0.2)',
                      borderColor: 'rgba(207, 32, 72, 0.45)',
                },
              }}
            >
                  <MinimizeIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

            <Menu
              id="yovo-assist-header-menu"
              anchorEl={headerMenuAnchor}
              open={Boolean(headerMenuAnchor)}
              onClose={() => setHeaderMenuAnchor(null)}
              disableScrollLock
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.75,
                    minWidth: 180,
                    bgcolor: '#1A1A1E',
                    color: '#fff',
                    borderRadius: '12px',
                    border: '1px solid rgba(207, 32, 72, 0.28)',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
                    backgroundImage: 'none',
                  },
                },
              }}
            >
              <MenuItem
                onClick={clearChatHistory}
                sx={{
                  fontSize: '0.875rem',
                  py: 1.1,
                  gap: 0.5,
                  '&:hover': { bgcolor: 'rgba(207, 32, 72, 0.12)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <DeleteIcon sx={{ fontSize: 18, color: YOVO_CHAT_ACCENT }} />
                </ListItemIcon>
                <ListItemText
                  primary="Clear conversation"
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                />
              </MenuItem>
            </Menu>
      </Box>
      
      {/* Messages Area - Compact Design with Travel Animation */}
      <Box sx={{ 
        flex: 1,
        overflowY: 'auto', 
        px: 2,
        py: 1.5,
        minHeight: 0,
        background: 'transparent',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '2px',
        },
        // Firefox scrollbar
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
      }}>
        {/* Current Trip Brief - sticky at top */}
        {currentTripBrief && (
          <Box sx={{ mb: 1.25, position: 'sticky', top: 0, zIndex: 10, px: 0 }}>
            <TripBriefCard 
              brief={currentTripBrief} 
              onClose={() => setCurrentTripBrief(null)} 
            />
          </Box>
        )}
        
        {showWelcomeHero && (
          <Box sx={{ mb: showWelcomeQuickActions ? 0 : 1, flexShrink: 0 }}>
            <EmptyStateWelcomeHero />
          </Box>
        )}

        {showWelcomeQuickActions ? (
          <>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1, minHeight: 8 }} />
            </Box>
            <EmptyStateQuickActions
              onSelect={(action) => {
                if (action?.localBotReply) {
                  const userText = String(action.sendText || '').trim();
                  const botText = String(action.localBotReply || '').trim();
                  if (!userText || !botText) return;
                  const now = new Date();
                  setMessages((prev) => [
                    ...prev,
                    { from: 'user', text: userText, timestamp: now },
                    { from: 'bot', text: botText, timestamp: new Date(), error: null },
                  ]);
                  setConversationHistory((prev) => [
                    ...prev,
                    { role: 'user', content: userText },
                    { role: 'assistant', content: botText },
                  ]);
                  return;
                }
                if (handleSendRef.current && action?.sendText) {
                  handleSendRef.current(action.sendText);
                }
              }}
            />
          </>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
        {renderedMessages}
          
            {/* Loading / waiting — same working indicator (existing isLoading flag) */}
          {isLoading && (
              <Box sx={{ mb: 1.75, display: 'flex', justifyContent: 'flex-start', px: 0.25 }}>
                <YovoWorkingIndicator />
            </Box>
          )}
          <div ref={messagesEndRef} />
          </Box>
        )}
        </Box>
      
      {/* Interactive Form Area */}
      {showInteractiveForm && (
      <Box sx={{ 
        p: 2, 
        bgcolor: 'transparent',
        borderTop: '1px solid rgba(220, 200, 220, 0.35)',
        borderBottom: '1px solid rgba(220, 200, 220, 0.35)',
        position: 'relative'
      }}>
        {/* Close Button for Interactive Form */}
        <IconButton 
          onClick={() => {
            setShowInteractiveForm(false);
            setInteractiveFormType('');
            setDepartureCity(null);
          }}
          sx={{ 
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#666',
            '&:hover': { 
              color: YOVO_CHAT_ACCENT,
              bgcolor: 'rgba(171, 38, 69, 0.1)' 
            },
            p: 0.5,
            minWidth: 'auto',
            zIndex: 1
          }}
          size="small"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
          {interactiveFormType === 'trip_type' && (
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#333', mb: 1 }}>
                Select Trip Type
              </FormLabel>
              <RadioGroup
                value={tripType}
                onChange={(e) => setTripType(e.target.value)}
                row
                sx={{ gap: 2 }}
              >
                <FormControlLabel 
                  value="oneway" 
                  control={<Radio size="small" />} 
                  label="One-way" 
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                />
                <FormControlLabel 
                  value="roundtrip" 
                  control={<Radio size="small" />} 
                  label="Round-trip" 
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                />
              </RadioGroup>
            </FormControl>
          )}

          {interactiveFormType === 'departure_city' && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#333', mb: 1 }}>
                Select Departure City
              </Typography>
              <Autocomplete
                freeSolo
                loading={departureCityLoading}
                options={departureCityOptions}
                value={departureCity}
                onChange={(event, newValue) => setDepartureCity(newValue)}
                onInputChange={(event, newInputValue) => {
                  if (newInputValue) {
                    loadDepartureCityOptions(newInputValue);
                  }
                }}
                getOptionLabel={(option) => option.name || option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search departure city..."
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.8rem',
                        '& fieldset': { borderColor: '#E0E0E0' },
                        '&:hover fieldset': { borderColor: YOVO_CHAT_ACCENT },
                        '&.Mui-focused fieldset': { borderColor: YOVO_CHAT_ACCENT }
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.city}, {option.country}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Box>
          )}
            </Box>
          )}

      {/* Input Area — same pill as homepage Assist trigger */}
        <Box sx={{ 
        px: 1.75,
        py: 1.5,
        display: 'flex', 
        alignItems: 'center',
        flexShrink: 0,
        borderBottomLeftRadius: flushBottom ? 0 : YOVO_CHAT_SHELL_RADIUS,
        borderBottomRightRadius: flushBottom ? 0 : YOVO_CHAT_SHELL_RADIUS,
        position: 'relative',
        background: 'transparent',
      }}>
        <Box
          sx={{
          flex: 1,
            position: 'relative',
          display: 'flex',
          alignItems: 'center',
            background: '#fff',
            borderRadius: '25px',
            border: '2px solid transparent',
            backgroundImage: `linear-gradient(#fff, #fff), ${YOVO_ASSIST_INPUT_BORDER_GRAD}`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            overflow: 'hidden',
            minHeight: 48,
            boxSizing: 'border-box',
            opacity: isLoading ? 0.72 : 1,
            transition: 'opacity 0.15s ease',
          }}
        >
          <Box
            component="input"
            type="text"
            placeholder="How can Yovo Assist help you?"
          value={input}
            onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
            autoComplete="off"
            aria-label="Message Yovo Assist"
          sx={{ 
            flex: 1, 
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              padding: '12px 16px 12px 20px',
              fontFamily: 'Host Grotesk, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: 1.25,
              color: '#333',
              '&::placeholder': {
                color: '#717180',
                opacity: 1,
              },
              '&:disabled': {
                cursor: 'not-allowed',
              },
            }}
          />
          {isLoading ? (
            <CircularProgress size={18} sx={{ color: '#888', mr: 1.5, flexShrink: 0 }} />
          ) : null}
          <Box
            component="button"
            type="button"
          onClick={() => handleSend()}
          disabled={!input.trim() || isLoading}
            aria-label="Send message"
          sx={{ 
              width: 40,
              height: 40,
              m: '4px',
              p: 0,
              border: 'none',
              background: '#000',
              borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
              flexShrink: 0,
              cursor: !input.trim() || isLoading ? 'not-allowed' : 'pointer',
              opacity: !input.trim() || isLoading ? 0.45 : 1,
              transition: 'background 0.15s ease, opacity 0.15s ease',
            '&:hover': {
                background: !input.trim() || isLoading ? '#000' : '#333',
              },
            }}
          >
            <Box
              component="img"
              src={playicon}
              alt=""
              aria-hidden
              sx={{
                width: 24,
                height: 24,
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
        </Box>
      </Box>
          </>
        {/* )} */}
      </Box>
    </Box>
  );
});

export default Chatbot; 